import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  signInWithCredential,
  linkWithCredential,
  linkWithPopup,
  EmailAuthProvider,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  getIdTokenResult,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, googleProvider, db, functions } from '../firebase';
import { profileIsBlack, profileNeedsPaymentUpdate, MEMBER_ID_PATTERN } from '../utils/membership';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // The Firebase account, which for a guest is an anonymous one. Kept apart
  // from `user` below: every existing screen treats `user` as "signed in", and
  // a guest who has only left a comment must still see Sign In, be bounced
  // from /profile, and so on.
  const [fbUser, setFbUser] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  // users/{uid} for a guest. Created server-side on their first post
  // (functions/reviews.js loadAuthor), so null until they have done something.
  const [guestProfile, setGuestProfile] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // The "claim your account" dialog is opened from several places (the
  // banner, the navbar, the comment box after a guest's 1st and 3rd post), so
  // it lives here and ClaimAccount in App.jsx renders the one instance.
  const [claimOpen, setClaimOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // Admin access carried as a custom claim on the account. Held separately
  // from userProfile because it lives on the auth token, not the Firestore doc.
  const [hasAdminClaim, setHasAdminClaim] = useState(false);
  // True from the moment an account is created until its full profile — name
  // and role included — has been written. The auth-state listener fires in that
  // gap and must not race a partial profile in behind it. A ref, not state,
  // because the listener reads it in the same tick the account is created.
  const signupInFlightRef = useRef(false);

  // memberId is server-owned now — firestore.rules rejects client writes to it,
  // so accounts created before member IDs existed can only be backfilled here.
  // That backfill is why the Profile page always has a member ID to show.
  async function withMemberId(profile) {
    if (!profile || MEMBER_ID_PATTERN.test(profile.memberId || '')) return profile;
    try {
      const ensureMemberId = httpsCallable(functions, 'ensureMemberId');
      const res = await ensureMemberId();
      return res.data?.memberId ? { ...profile, memberId: res.data.memberId } : profile;
    } catch (e) {
      console.warn('Could not assign a member ID:', e.message);
      return profile;
    }
  }

  async function fetchUserProfile(uid) {
    try {
      const ref = doc(db, 'users', uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const profile = await withMemberId(snap.data());
        setUserProfile(profile);
        return profile;
      }
      // Profile missing. Two very different reasons for that, and only one of
      // them should be repaired here.
      //
      // Creating an account fires onAuthStateChanged immediately, which lands
      // this function here — while signUpWithEmail is still a few awaits away
      // from writing the real profile. This path knows only what is on the
      // Firebase auth record: no role, and no display name yet, because
      // updateProfile has not run. Both writers used a plain setDoc, so it was
      // last-write-wins, and when this one won a restaurant owner was recorded
      // as an ordinary diner with a blank name.
      if (signupInFlightRef.current) return null;

      // Genuinely missing: an account that predates the rules, or one whose
      // signup died partway. Backfill it.
      const fbUser = auth.currentUser;
      if (fbUser) {
        return await backfillUserProfile(uid, fbUser);
      }
      return null;
    } catch (e) {
      console.warn('Could not fetch user profile:', e.message);
      return null;
    }
  }

  async function createUserProfile(uid, data) {
    const profile = {
      uid,
      email: data.email,
      displayName: data.displayName || '',
      // Every account is on Volcyy (free) by default — no payment required.
      // Volcyy Black is the paid upgrade; membershipStatus/membershipExpiry
      // below describe that Black subscription specifically. These three are
      // pinned to exactly these values by firestore.rules on create.
      membershipTier: 'free',
      membershipStatus: 'inactive',
      membershipExpiry: null,
      // 'member' unless signing up through the /business flow, which passes
      // role:'business_owner' so the dashboard reads correctly right away
      // (submitRestaurantListing also sets this server-side on first submit).
      role: data.role || 'member',
      createdAt: serverTimestamp(),
    };
    await setDoc(doc(db, 'users', uid), profile);
    // Needs the doc to exist first — ensureMemberId reads it before stamping.
    const withId = await withMemberId(profile);
    setUserProfile(withId);
    return withId;
  }

  // Repairs a signed-in account that has no users/{uid} document, using only
  // what the Firebase auth record knows.
  //
  // Merges, and writes no field it cannot vouch for: no displayName when the
  // auth record has none, and no role at all — role is chosen at signup and set
  // by the business-listing functions, so a confident 'member' written here
  // could demote a restaurant owner. Between this and the signup guard above,
  // neither writer can now undo the other.
  async function backfillUserProfile(uid, fbUser) {
    const profile = {
      uid,
      email: fbUser.email,
      membershipTier: 'free',
      membershipStatus: 'inactive',
      membershipExpiry: null,
      createdAt: serverTimestamp(),
    };
    if (fbUser.displayName) profile.displayName = fbUser.displayName;
    await setDoc(doc(db, 'users', uid), profile, { merge: true });
    const withId = await withMemberId(profile);
    setUserProfile(withId);
    return withId;
  }

  async function handleGoogleUser(fbUser, role) {
    const ref = doc(db, 'users', fbUser.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await createUserProfile(fbUser.uid, {
        email: fbUser.email,
        displayName: fbUser.displayName,
        role,
      });
      return true;
    }
    // Through withMemberId like every other read path, so a returning Google
    // user missing a member ID gets one here rather than waiting for the
    // auth-state listener's fetch to come round.
    setUserProfile(await withMemberId(snap.data()));
    return false;
  }

  // ---- Guests -------------------------------------------------------------
  //
  // A visitor can post without signing up. The first time they do, the page
  // calls ensureGuest(), which makes a Firebase anonymous account; everything
  // they post hangs off its uid. Signing up later LINKS a real credential onto
  // that same account (so nothing moves), and signing in to an account that
  // already exists MERGES the guest's posts across (functions/accounts.js).

  // Returns the current Firebase user, creating a guest if nobody is signed in.
  async function ensureGuest() {
    if (auth.currentUser) return auth.currentUser;
    const result = await signInAnonymously(auth);
    return result.user;
  }

  // Re-reads the guest's profile, e.g. after their first post creates it.
  const refreshGuestProfile = useCallback(async () => {
    const current = auth.currentUser;
    if (!current?.isAnonymous) return null;
    try {
      const snap = await getDoc(doc(db, 'users', current.uid));
      const profile = snap.exists() ? snap.data() : null;
      setGuestProfile(profile);
      return profile;
    } catch {
      return null;
    }
  }, []);

  // Linking a credential turns the anonymous account into a real one without
  // firing onAuthStateChanged (same user, same uid), so the state the listener
  // would have set is set here.
  async function finishClaim(fbAccount, { displayName, role }) {
    // Forced refresh so the token the function sees no longer says
    // "anonymous" — claimGuestAccount refuses anonymous callers.
    await fbAccount.getIdToken(true);
    const claimGuestAccount = httpsCallable(functions, 'claimGuestAccount');
    await claimGuestAccount({ displayName, role });
    setIsAnonymous(false);
    setFbUser(fbAccount);
    setGuestProfile(null);
    return fetchUserProfile(fbAccount.uid);
  }

  // Moves a guest's activity onto the account just signed in to. Never fails
  // the sign-in: the person is in their account either way, and a guest whose
  // posts didn't move is a worse outcome than a sign-in that errors.
  async function mergeGuestInto(guestIdToken) {
    if (!guestIdToken) return null;
    try {
      const mergeGuestAccount = httpsCallable(functions, 'mergeGuestAccount');
      const res = await mergeGuestAccount({ guestIdToken });
      return res.data;
    } catch (e) {
      console.warn('Could not move guest posts to this account:', e.message);
      return null;
    }
  }

  // The guest's ID token, taken BEFORE switching accounts — once signed in as
  // someone else the guest session is gone. It is what proves to the merge
  // function that these posts are the caller's.
  async function guestTokenForMerge() {
    const current = auth.currentUser;
    return current?.isAnonymous ? current.getIdToken() : null;
  }

  async function signInWithGoogle(role) {
    signupInFlightRef.current = true;
    try {
      const current = auth.currentUser;
      if (current?.isAnonymous) {
        try {
          const result = await linkWithPopup(current, googleProvider);
          await finishClaim(result.user, { displayName: result.user.displayName, role });
          return { ...result, isNewUser: true, claimedGuest: true };
        } catch (err) {
          // This Google account is already a Volcyy account: sign in to it and
          // bring the guest's posts along.
          if (err.code !== 'auth/credential-already-in-use') throw err;
          const credential = GoogleAuthProvider.credentialFromError(err);
          if (!credential) throw err;
          const guestToken = await current.getIdToken();
          const result = await signInWithCredential(auth, credential);
          const isNewUser = await handleGoogleUser(result.user, role);
          const merged = await mergeGuestInto(guestToken);
          return { ...result, isNewUser, merged };
        }
      }
      const result = await signInWithPopup(auth, googleProvider);
      const isNewUser = await handleGoogleUser(result.user, role);
      return { ...result, isNewUser };
    } finally {
      signupInFlightRef.current = false;
    }
  }

  async function signUpWithEmail(email, password, displayName, role) {
    signupInFlightRef.current = true;
    try {
      const current = auth.currentUser;
      if (current?.isAnonymous) {
        // An email that already has an account fails here with
        // auth/email-already-in-use; AuthModal points them at Sign In, which
        // merges instead.
        const result = await linkWithCredential(current, EmailAuthProvider.credential(email, password));
        await updateProfile(result.user, { displayName });
        await finishClaim(result.user, { displayName, role });
        resendVerification().catch((err) => console.warn('Could not send the confirmation email:', err.message));
        return { ...result, claimedGuest: true };
      }
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });
      await createUserProfile(result.user.uid, { email, displayName, role });
      // Not awaited and never fatal — see resendVerification.
      resendVerification().catch((err) => console.warn('Could not send the confirmation email:', err.message));
      return result;
    } finally {
      signupInFlightRef.current = false;
    }
  }

  // Posts the confirm-your-email link. Best effort at sign-up: an account
  // that exists with no mail sent is recoverable (the banner resends), an
  // account that failed to be created is not.
  async function resendVerification() {
    const send = httpsCallable(functions, 'sendVerificationEmail');
    const res = await send({});
    return res.data;
  }

  async function signInWithEmail(email, password) {
    const guestToken = await guestTokenForMerge();
    const result = await signInWithEmailAndPassword(auth, email, password);

    // Moving the guest's posts across is bookkeeping, not part of signing in.
    // Waiting for it held the sign-in dialog open for several seconds — longer
    // on a cold function — while the person was already signed in. So it runs
    // on its own and refreshes the profile when it lands; mergeGuestInto never
    // throws, and a merge that doesn't finish leaves the posts where they are
    // rather than losing them.
    const merging = mergeGuestInto(guestToken).then(async (merged) => {
      if (merged?.merged) await fetchUserProfile(result.user.uid);
      return merged;
    });

    await fetchUserProfile(result.user.uid);
    return { ...result, merging };
  }

  async function resetPassword(email) {
    // Sends a branded reset email via our Cloud Function (Brevo), instead of
    // Firebase's default template. The function links to /reset-password.
    const sendPasswordReset = httpsCallable(functions, 'sendPasswordReset');
    await sendPasswordReset({ email });
  }

  async function logout() {
    await signOut(auth);
    setUserProfile(null);
  }

  // The account's admin claim, and nothing else — the same single source that
  // callerIsAdmin in functions/index.js and isAdmin() in firestore.rules read.
  const isAdmin = hasAdminClaim;
  const isBlack = profileIsBlack(userProfile);
  // True while Stripe is retrying a declined renewal. isBlack is also true
  // during this window — the member keeps their discounts while they fix the
  // card — so these two are read together, not as alternatives.
  const needsPaymentUpdate = profileNeedsPaymentUpdate(userProfile);
  const effectiveTier = isBlack ? 'black' : 'free';

  // Stable identity so effects that depend on it (e.g. the post-payment
  // confirmation poll) don't tear down and restart every time the profile
  // updates. Reads auth.currentUser fresh on each call, and resolves with the
  // freshly-read profile so a caller can act on it without waiting a render
  // for the new isBlack to reach it.
  const refreshProfile = useCallback(() => {
    const current = auth.currentUser;
    return current && !current.isAnonymous ? fetchUserProfile(current.uid) : Promise.resolve(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setFbUser(fbUser);
      setIsAnonymous(!!fbUser?.isAnonymous);
      if (fbUser?.isAnonymous) {
        // A guest: no admin claim, no member profile to backfill (a guest
        // profile is only ever made by the server, on their first post).
        setHasAdminClaim(false);
        setUserProfile(null);
        await refreshGuestProfile();
      } else if (fbUser) {
        setGuestProfile(null);
        // Forced refresh (`true`): an ID token is valid for an hour and only
        // picks up new claims when it renews, so without this a member who was
        // just granted admin would keep seeing the ordinary site until their
        // old token expired — and would report it as broken. Refreshing on
        // load makes signing out and back in a reliable fix.
        try {
          const token = await getIdTokenResult(fbUser, true);
          setHasAdminClaim(token.claims?.admin === true);
        } catch {
          setHasAdminClaim(false);
        }
        await fetchUserProfile(fbUser.uid);
      } else {
        setUserProfile(null);
        setGuestProfile(null);
        setHasAdminClaim(false);
      }
      setLoading(false);
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fbUser || fbUser.isAnonymous) return;
    return onSnapshot(doc(db, 'users', fbUser.uid), snap => {
      if (snap.exists()) setUserProfile(snap.data());
    }, error => console.warn('Could not refresh profile:', error.message));
  }, [fbUser]);

  const isRestaurantOwner = ['business_owner', 'restaurant_owner'].includes(userProfile?.role);
  const user = fbUser && !isAnonymous ? fbUser : null;
  const isGuest = !!fbUser && isAnonymous;

  const value = {
    user,
    // Guests. isGuest is true for an anonymous session; guestProfile (with the
    // generated handle) exists once they have posted, liked or reported —
    // which is when there is something worth claiming.
    isGuest,
    guestProfile,
    // uid of whoever is acting, guest or signed in — for "is this my post" and
    // "have I liked this". null before anyone has posted or signed in.
    currentUid: fbUser?.uid || null,
    ensureGuest,
    refreshGuestProfile,
    claimOpen,
    openClaim: () => setClaimOpen(true),
    closeClaim: () => setClaimOpen(false),
    userProfile,
    loading,
    isAdmin,
    isBlack,
    needsPaymentUpdate,
    effectiveTier,
    isRestaurantOwner,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    resetPassword,
    logout,
    refreshProfile,
    // Google and Apple hand back an address their provider already
    // checked, so only password sign-ups ever show as unconfirmed.
    emailVerified: !user || user.emailVerified !== false,
    resendVerification,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Rendered immediately rather than held back until the sign-in check
          resolves. Blocking here blanked the entire site — menu, listings and
          footer — while Firebase Auth started up, and longest for signed-in
          members whose token needs a round trip. It also threw away the
          prerendered HTML a visitor had already been shown, so the sequence
          was content, blank, content. Screens that genuinely need to know who
          you are wait for `loading` themselves via RequiresAuth in App.jsx. */}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
